import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';
import { useApplyAffiliateMutation } from '../../redux/thunks/unauth/applyaffiliate';
import {
  Box, Container, Grid, Paper, Typography, Button, TextField, MenuItem,
  Stack, Chip, Divider, Alert, useTheme, useMediaQuery,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as XIcon,
  AttachMoney as MoneyIcon,
  AllInclusive as InfinityIcon,
  Cookie as CookieIcon,
} from '@mui/icons-material';

const APPLICANT_TYPES = [
  { value: 'agency',           label: 'Marketing or social media agency' },
  { value: 'coach',            label: 'Business coach or educator' },
  { value: 'creator',          label: 'Content creator (social, podcast, newsletter)' },
  { value: 'consultant',       label: 'Local SEO / marketing consultant' },
  { value: 'community_leader', label: 'Community or industry group leader' },
  { value: 'other',            label: 'Other' },
];

const AUDIENCE_SIZES = [
  { value: 'under_5k',  label: 'Under 5,000' },
  { value: '5k_25k',    label: '5,000 — 25,000' },
  { value: '25k_100k',  label: '25,000 — 100,000' },
  { value: 'over_100k', label: 'Over 100,000' },
];

const IDEAL_FITS = [
  'Local marketing coaches and educators',
  'Agency owners with local business clients',
  'Content creators serving brick-and-mortar businesses',
  'Local SEO consultants and GBP experts',
  'Industry podcast hosts and newsletter writers',
];

const NOT_FITS = [
  'Sub-affiliate networks',
  'Coupon and discount sites',
  'Audiences primarily other affiliate marketers',
  'Audiences without local business owner overlap',
];

const FAQ_ITEMS = [
  {
    q: 'How is attribution tracked?',
    a: 'Every approved partner gets a unique link (revfer.com/r/yourcode). When someone clicks it, we set a 90-day attribution cookie. If they sign up and become a paying customer within that window, you earn 20% of their subscription for as long as they remain a paying customer.'
  },
  {
    q: 'How long do I earn commission on each referral?',
    a: 'For as long as your referred customer keeps paying Revfer. Unlike most affiliate programs that cap commissions at 12 months, we pay you the full customer lifetime. If they stay 5 years, you earn for 5 years.'
  },
  {
    q: 'When do I get paid?',
    a: 'Commissions are calculated monthly and paid after a 30-day hold period (to account for refunds). Payouts go out by the 15th of the following month.'
  },
  {
    q: 'What happens if a customer churns or refunds?',
    a: 'Commission stops when the customer cancels. Refunds within 30 days are clawed back from your pending balance. Anything past the hold period is yours to keep.'
  },
  {
    q: 'How do my referrals sign up — do I do it for them?',
    a: 'Each business signs up individually using your referral link. Revfer needs to connect directly to each customer\'s Google Business Profile, Facebook, Instagram, and other accounts via OAuth — which means the business owner needs to authorize the connections themselves. You can absolutely help them through it as part of your service, but the account itself belongs to them.'
  },
  {
    q: 'Can I white-label Revfer for my clients?',
    a: 'Not currently. Customers see the Revfer brand throughout their experience. If you\'re looking to offer review automation as part of your agency\'s branded service, the best fit is to position Revfer as a partner product your clients use (with your strategic guidance) rather than your own product.'
  },
  {
    q: 'Can I promote through paid ads?',
    a: 'Yes, with one rule: no bidding on the Revfer brand name. Drive traffic from your own audiences, content, and channels.'
  },
  {
    q: 'Do I need to disclose the affiliate relationship?',
    a: 'Yes. Both because the FTC requires it, and because authentic disclosure builds more trust with your audience than hiding it.'
  },
  {
    q: 'What if multiple partners refer the same customer?',
    a: 'First-touch wins. Whichever partner the customer clicked through first gets the attribution, for the full lifetime of that customer\'s subscription.'
  },
  {
    q: 'Can I refer customers I already know?',
    a: 'Yes, provided they sign up through your unique link. Self-referrals (referring yourself or businesses you own) are not eligible.'
  },
  {
    q: 'What about competing products?',
    a: 'While in the program, you cannot actively promote direct competitors (review management or AI content tools targeting the same audience). You can mention them in editorial content.'
  },
  {
    q: 'How long does application review take?',
    a: 'We aim to review every application within 5 business days. We may follow up with questions before deciding.'
  },
];

const AffiliatesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    platform_url: '',
    applicant_type: '',
    audience_description: '',
    audience_size: '',
    why_fit: '',
    website: '', // honeypot
  });

  const [errors, setErrors]   = useState({});
  const [success, setSuccess] = useState(false);
  const [applyAffiliate, { isLoading }] = useApplyAffiliateMutation();

  const validate = () => {
    const errs = {};
    if (!formData.full_name.trim())                                errs.full_name = 'Required';
    if (!formData.email.trim())                                     errs.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))    errs.email = 'Enter a valid email';
    if (!formData.platform_url.trim())                              errs.platform_url = 'Required';
    if (!formData.applicant_type)                                   errs.applicant_type = 'Required';
    if (!formData.audience_description.trim() ||
        formData.audience_description.trim().length < 30)
      errs.audience_description = 'Please give at least 30 characters of detail';
    if (!formData.audience_size)                                    errs.audience_size = 'Required';
    if (!formData.why_fit.trim() || formData.why_fit.trim().length < 30)
      errs.why_fit = 'Please give at least 30 characters of detail';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await applyAffiliate({ payload: formData }).unwrap();
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Application error:', err);
      setErrors({ submit: err?.data?.error || 'Something went wrong. Please try again.' });
    }
  };

  const setField = (k, v) => {
    setFormData(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined }));
  };

  return (
    <>
      <EnhancedSEO
        title="Revfer Partner Program — 20% Lifetime Commission"
        description="Refer Revfer to your audience of local service businesses. Earn 20% recurring commission for the full lifetime of every customer you refer. Built for marketing coaches, agencies, and creators."
        type="website"
      />

      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>

        {/* HERO */}
        <Box sx={{
          background: 'linear-gradient(135deg, #0D2B6B 0%, #1976D2 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          px: 2,
        }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Chip
              label="Partner Program"
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', mb: 3, fontWeight: 700 }}
            />
            <Typography variant="h2" sx={{
              fontWeight: 800,
              fontSize: { xs: '1.8rem', md: '3rem' },
              mb: 2,
              lineHeight: 1.2,
            }}>
              Refer Revfer to your audience.<br />
              Earn 20% for the lifetime of every customer.
            </Typography>
            <Typography variant="h6" sx={{
              opacity: 0.9,
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}>
              Built for marketing educators, agency owners, and creators serving
              local service businesses where reviews and reputation drive customers.
              No 12-month cap. Earn as long as your referral stays a customer.
            </Typography>
            <Button
              href="#apply"
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#0D2B6B',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#F0F7FF' }
              }}
            >
              Apply to become a partner
            </Button>
          </Container>
        </Box>

        {/* ECONOMICS — 3 cards */}
        <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={3}>
            {[
              { icon: MoneyIcon,    title: '20%',       sub: 'Recurring commission on customer MRR' },
              { icon: InfinityIcon, title: 'Lifetime',  sub: 'Earn as long as the customer is active' },
              { icon: CookieIcon,   title: '90 days',   sub: 'Attribution window from click' },
            ].map(({ icon: Icon, title, sub }) => (
              <Grid key={title} item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid #E2E8F0', borderRadius: 2, height: '100%' }}>
                  <Icon sx={{ fontSize: 36, color: '#1976D2', mb: 1 }} />
                  <Typography variant="h4" fontWeight={800} color="#0D2B6B" sx={{ mb: 0.5 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sub}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* WHO THIS IS FOR + NOT FOR */}
        <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 7 } }}>
          <Container maxWidth="md">
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#0D2B6B' }}>
                  Who this is built for
                </Typography>
                <Stack spacing={1.5}>
                  {IDEAL_FITS.map(item => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <CheckIcon sx={{ color: '#2E7D32', fontSize: 20, mt: 0.25 }} />
                      <Typography variant="body1">{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#0D2B6B' }}>
                  Who this isn't for
                </Typography>
                <Stack spacing={1.5}>
                  {NOT_FITS.map(item => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <XIcon sx={{ color: '#90A4AE', fontSize: 20, mt: 0.25 }} />
                      <Typography variant="body1" color="text.secondary">{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* HOW IT WORKS */}
        <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 4, color: '#0D2B6B', textAlign: 'center' }}>
            How it works
          </Typography>
          <Grid container spacing={3}>
            {[
              { n: 1, t: 'Apply',         d: 'Submit the form below — takes about 3 minutes.' },
              { n: 2, t: 'Get reviewed',  d: 'We review every application within 5 business days.' },
              { n: 3, t: 'Get your link', d: 'On approval, you get a unique partner link and welcome kit.' },
              { n: 4, t: 'Earn',          d: '20% of customer MRR, paid monthly, for the full customer lifetime.' },
            ].map(({ n, t, d }) => (
              <Grid key={n} item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '50%',
                    bgcolor: '#1976D2', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 20, mx: 'auto', mb: 1.5
                  }}>
                    {n}
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>{t}</Typography>
                  <Typography variant="body2" color="text.secondary">{d}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* FAQ */}
        <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 7 } }}>
          <Container maxWidth="md">
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#0D2B6B' }}>
              Frequently asked questions
            </Typography>
            {FAQ_ITEMS.map((item, idx) => (
              <Accordion key={idx} elevation={0} sx={{ border: '1px solid #E2E8F0', '&:before': { display: 'none' }, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Container>
        </Box>

        {/* APPLICATION FORM */}
        <Container maxWidth="sm" sx={{ py: { xs: 5, md: 7 } }} id="apply">
          {success ? (
            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', border: '1px solid #E2E8F0', borderRadius: 2 }}>
              <CheckIcon sx={{ fontSize: 56, color: '#2E7D32', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5, color: '#0D2B6B' }}>
                Application received
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Thanks, {formData.full_name.split(' ')[0]}. We'll review and reach out
                at {formData.email} within 5 business days.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Feel free to share the program — <strong>revfer.com/affiliates</strong>
              </Typography>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid #E2E8F0', borderRadius: 2 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: '#0D2B6B' }}>
                Apply to the partner program
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Takes about 3 minutes. We review every application.
              </Typography>

              <Stack spacing={2.5}>
                <TextField
                  label="Full name"
                  value={formData.full_name}
                  onChange={(e) => setField('full_name', e.target.value)}
                  error={!!errors.full_name}
                  helperText={errors.full_name}
                  fullWidth
                  required
                />

                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setField('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  fullWidth
                  required
                />

                <TextField
                  label="Where can we see your work?"
                  placeholder="Instagram, podcast, agency site, LinkedIn, newsletter — any one URL"
                  value={formData.platform_url}
                  onChange={(e) => setField('platform_url', e.target.value)}
                  error={!!errors.platform_url}
                  helperText={errors.platform_url || 'Any URL where we can see your audience and content'}
                  fullWidth
                  required
                />

                <TextField
                  select
                  label="Who are you?"
                  value={formData.applicant_type}
                  onChange={(e) => setField('applicant_type', e.target.value)}
                  error={!!errors.applicant_type}
                  helperText={errors.applicant_type}
                  fullWidth
                  required
                >
                  {APPLICANT_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Approximate audience size"
                  value={formData.audience_size}
                  onChange={(e) => setField('audience_size', e.target.value)}
                  error={!!errors.audience_size}
                  helperText={errors.audience_size}
                  fullWidth
                  required
                >
                  {AUDIENCE_SIZES.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Describe your audience"
                  placeholder="e.g., I teach 190+ local service business owners how to grow on Instagram"
                  value={formData.audience_description}
                  onChange={(e) => setField('audience_description', e.target.value)}
                  error={!!errors.audience_description}
                  helperText={errors.audience_description || `${formData.audience_description.length}/300`}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 300 }}
                  fullWidth
                  required
                />

                <TextField
                  label="Why is Revfer a fit for your audience?"
                  placeholder="What problem does Revfer solve for the people you serve?"
                  value={formData.why_fit}
                  onChange={(e) => setField('why_fit', e.target.value)}
                  error={!!errors.why_fit}
                  helperText={errors.why_fit || `${formData.why_fit.length}/300`}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 300 }}
                  fullWidth
                  required
                />

                {/* Honeypot — visually hidden, bots fill it */}
                <Box sx={{ position: 'absolute', left: '-9999px', height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => setField('website', e.target.value)}
                  />
                </Box>

                {errors.submit && (
                  <Alert severity="error">{errors.submit}</Alert>
                )}

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  sx={{ py: 1.5, fontWeight: 700 }}
                  startIcon={isLoading && <CircularProgress size={18} color="inherit" />}
                >
                  {isLoading ? 'Submitting...' : 'Submit application'}
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                  By applying you agree to the program's terms.
                  We'll review within 5 business days.
                </Typography>
              </Stack>
            </Paper>
          )}
        </Container>
      </Box>
    </>
  );
};

export default AffiliatesPage;